import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Phone, MessageCircle, Mail, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { PolicyDialog } from "@/components/legal";
import { useLang } from "@/i18n";

const API = `/api`;
const WHATSAPP = "393316819146";
const PHONE_DISPLAY = "+39 331 6819146";

const emptyForm = {
  nome: "", azienda: "", email: "", telefono: "",
  provincia: "", prodotto: "", formato: "", quantita: "", messaggio: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/60">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full bg-white border border-ink/80 rounded-none px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-ferrari focus:ring-1 focus:ring-ferrari transition-colors duration-200";

export default function QuoteForm() {
  const { t, lang } = useLang();
  const q = t.quote;
  const [tab, setTab] = useState("rivenditore");
  const [form, setForm] = useState(emptyForm);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const [website, setWebsite] = useState("");
  const [startedAt, setStartedAt] = useState(Date.now());

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const switchTab = (tk) => {
    setTab(tk);
    setForm({ ...form, formato: tk === "gruppo_acquisto" ? q.fixedFornitura : "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.azienda || !form.email || !form.telefono || !form.provincia || !form.prodotto) {
      toast.error(q.errRequired);
      return;
    }
    if (tab !== "gruppo_acquisto" && !form.formato) {
      toast.error(q.errFormato);
      return;
    }
    if (!privacy) {
      toast.error(q.errPrivacy);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        tipo_cliente: tab,
        privacy_consent: true,
        lang,
        formato: tab === "gruppo_acquisto" ? q.fixedFornitura : form.formato,

        // Anti-spam
        website,
        started_at: startedAt
      };
      const res = await axios.post(`${API}/quote.php`, payload);
      if (res.data.status === "success") {
        toast.success(q.ok);
        setForm({ ...emptyForm, formato: tab === "gruppo_acquisto" ? q.fixedFornitura : "" });
        setPrivacy(false);
        setWebsite("");
        setStartedAt(Date.now());
      }
    } catch (err) {
      toast.error(q.errSend);
    } finally {
      setLoading(false);
    }
  };

  const tabBtn = (key, label, withBorder) =>
    `py-4 md:py-5 px-1 font-bold uppercase tracking-[0.08em] text-[10px] md:text-sm transition-colors duration-200 ${withBorder ? "border-l border-ink/80" : ""} ${tab === key ? "bg-ferrari text-white" : "bg-white text-ink hover:bg-[#f4f4f4]"}`;

  return (
    <section id="preventivo" className="py-20 md:py-28 bg-ink text-white">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-ferrari">{q.label}</span>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-[0.88]">
              {q.titleA}<span className="text-ferrari">{q.titleB}</span>
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-md">
              {q.subtitlePre}<strong className="text-white">{q.subtitleStrong1}</strong>{q.subtitleMid}<strong className="text-white">{q.subtitleStrong2}</strong>{q.subtitlePost}
            </p>

            <div className="mt-10 space-y-3">
              <a
                data-testid="whatsapp-link"
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Salve, vorrei un preventivo per il pellet.")}`}
                target="_blank" rel="noopener noreferrer"
                className="group flex items-center justify-between border border-white/25 px-5 py-4 hover:border-ferrari hover:bg-ferrari transition-colors duration-200 rounded-xl"
              >
                <span className="flex items-center gap-3 font-semibold"><MessageCircle size={20} /> {q.whatsapp}</span>
                <span className="text-sm text-white/70 group-hover:text-white">{PHONE_DISPLAY}</span>
              </a>
              <a
                data-testid="phone-link"
                href={`tel:+${WHATSAPP}`}
                className="group flex items-center justify-between border border-white/25 px-5 py-4 hover:border-ferrari hover:bg-ferrari transition-colors duration-200 rounded-xl"
              >
                <span className="flex items-center gap-3 font-semibold"><Phone size={20} /> {q.call}</span>
                <span className="text-sm text-white/70 group-hover:text-white">{PHONE_DISPLAY}</span>
              </a>
              <a
                data-testid="email-link"
                href="mailto:info@pstpellet.com"
                className="group flex items-center justify-between border border-white/25 px-5 py-4 hover:border-ferrari hover:bg-ferrari transition-colors duration-200 rounded-xl"
              >
                <span className="flex items-center gap-3 font-semibold"><Mail size={20} /> {q.email}</span>
                <span className="text-sm text-white/70 group-hover:text-white">info@pstpellet.com</span>
              </a>
            </div>

            <div data-testid="enplus-guarantee" className="mt-6 flex items-center gap-4 border border-ferrari bg-ferrari grain px-5 py-4 rounded-2xl">
              <ShieldCheck size={30} className="text-white shrink-0" strokeWidth={1.8} />
              <div>
                <div className="font-display font-bold uppercase tracking-tight text-lg leading-none">{q.guaranteeTitle}</div>
                <div className="text-sm text-white/85 mt-1.5">{q.guaranteeDesc}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white text-ink border border-ink/80">
              <div className="grid grid-cols-3 border-b border-ink/80">
                <button data-testid="tab-rivenditore" onClick={() => switchTab("rivenditore")} className={tabBtn("rivenditore", q.tabs.rivenditore, false)}>{q.tabs.rivenditore}</button>
                <button data-testid="tab-gdo" onClick={() => switchTab("gdo")} className={tabBtn("gdo", q.tabs.gdo, true)}>{q.tabs.gdo}</button>
                <button data-testid="tab-gruppo" onClick={() => switchTab("gruppo_acquisto")} className={tabBtn("gruppo_acquisto", q.tabs.gruppo, true)}>{q.tabs.gruppo}</button>
              </div>

              <motion.form
                key={tab}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                onSubmit={submit}
                data-testid="quote-form"
                className="p-6 md:p-9 grid grid-cols-1 md:grid-cols-2 gap-5"
              >

              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden"
                }}
              >
              <label>
                Website
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>

                <Field label={q.fields.nome}>
                  <input data-testid="input-nome" className={inputCls} value={form.nome} onChange={set("nome")} placeholder={q.ph.nome} />
                </Field>
                <Field label={tab === "gruppo_acquisto" ? q.fields.aziendaGruppo : q.fields.azienda}>
                  <input data-testid="input-azienda" className={inputCls} value={form.azienda} onChange={set("azienda")} placeholder={tab === "gruppo_acquisto" ? q.ph.aziendaGruppo : q.ph.azienda} />
                </Field>
                <Field label={q.fields.email}>
                  <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder={q.ph.email} />
                </Field>
                <Field label={q.fields.telefono}>
                  <input data-testid="input-telefono" className={inputCls} value={form.telefono} onChange={set("telefono")} placeholder={q.ph.telefono} />
                </Field>
                <Field label={q.fields.provincia}>
                  <input data-testid="input-provincia" className={inputCls} value={form.provincia} onChange={set("provincia")} placeholder={q.ph.provincia} />
                </Field>
                <Field label={q.fields.prodotto}>
                  <select data-testid="select-prodotto" className={inputCls} value={form.prodotto} onChange={set("prodotto")}>
                    <option value="">{q.ph.prodottoSel}</option>
                    {q.prodotti.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>

                {tab !== "gruppo_acquisto" ? (
                  <Field label={q.fields.formato}>
                    <select data-testid="select-formato" className={inputCls} value={form.formato} onChange={set("formato")}>
                      <option value="">{q.ph.formatoSel}</option>
                      {q.formati.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>
                ) : (
                  <Field label={q.fields.fornitura}>
                    <input data-testid="input-formato-fixed" className={`${inputCls} bg-[#f4f4f4]`} value={q.fixedFornitura} readOnly />
                  </Field>
                )}
                <Field label={q.fields.quantita}>
                  <input data-testid="input-quantita" className={inputCls} value={form.quantita} onChange={set("quantita")} placeholder={tab === "gruppo_acquisto" ? q.ph.quantitaG : q.ph.quantitaR} />
                </Field>

                <div className="md:col-span-2">
                  <Field label={q.fields.messaggio}>
                    <textarea data-testid="input-messaggio" rows={3} className={inputCls} value={form.messaggio} onChange={set("messaggio")} placeholder={q.ph.messaggio} />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      data-testid="privacy-consent-checkbox"
                      checked={privacy}
                      onCheckedChange={(v) => setPrivacy(v === true)}
                      className="mt-0.5 rounded-none border-ink/80 data-[state=checked]:bg-ferrari data-[state=checked]:border-ferrari"
                    />
                    <span className="text-xs text-ink/70 leading-relaxed">
                      {q.privacyPre}
                      <PolicyDialog type="privacy">
                        <button type="button" data-testid="form-privacy-link" className="underline text-ink font-semibold hover:text-ferrari transition-colors">{q.privacyLink}</button>
                      </PolicyDialog>
                      {q.privacyPost}
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <button
                    data-testid="submit-quote-btn"
                    type="submit"
                    disabled={loading}
                    className="group w-full flex items-center justify-center gap-3 bg-ferrari text-white font-bold uppercase tracking-[0.15em] text-sm py-4 hover:bg-ink transition-colors duration-200 disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <>{q.submit} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" /></>}
                  </button>
                  <p className="text-[11px] text-ink/45 mt-3 text-center uppercase tracking-[0.1em]">{q.note}</p>
                </div>
              </motion.form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
